// Foundations
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';

// RXJS
import { startWith, map } from 'rxjs/operators';
import { Observable } from 'rxjs';

// Services
import {
  Breed,
  GetUserFirstNameDocument,
  Pet,
  PetBreed,
} from '../../../services/graphql.service';
import { UnitConversionPipe } from 'src/app/pipes/unit-conversion.pipe';
import { AnalyticsService } from 'src/app/services/analytics.service';

// CBW-784 Names
export interface PetTileFormInfo {
  id?: number;
  name: string;
  firstName: string;
  lastName: string;
  breeds: PetBreed[];
  weight: number;
  details?: string;
}

export interface InterviewRequestAgreement {
  id: number;
  message: string;
  errorMessage: string;
}

@Component({
  selector: '`app-pet-tile-form',
  templateUrl: './pet-tile-form.component.html',
  styleUrls: ['./pet-tile-form.component.scss'],
})
export class PetTileFormComponent implements OnInit, AfterViewChecked {
  @Input() form: FormGroup;
  @Input() petInfo: Pet;
  @Input() isErrorOnSave: boolean;
  @Input() breedList: Breed[];
  wholeBreedList: Breed[];
  isPetSelected = false;
  filteredBreeds: Array<Observable<Breed[]>> = [];

  imgUrl: string;

  agreementList: InterviewRequestAgreement[] = [
    {
      id: 0,
      message: 'At least 4 months old and spayed or neutered if over 7 months',
      errorMessage:
        'All dogs must be over 4 months old to attend Camp. Dogs over 7 months must also be spayed or neutered.',
    },
    {
      id: 1,
      message:
        'Current on Rabies, Distemper and Bordetella vaccines. I understand other vaccines might be required',
      errorMessage:
        'All dogs must be current on required vaccinations to attend Camp.',
    },
    {
      id: 2,
      message: 'Flea/tick free and in good overall health',
      errorMessage:
        'All dogs must be flea/tick free and in good overall health.',
    },
    {
      id: 3,
      message: 'Dog and people friendly',
      errorMessage: 'All dogs must be dog and people friendly to attend Camp.',
    },
  ];

  constructor(
    private fb: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private convertUnit: UnitConversionPipe,
    private _analytics: AnalyticsService
  ) {}

  ngOnInit() {
    this.wholeBreedList = this.breedList;
    this.agreementList.forEach((_) => {
      this.addAgreement();
    });
    if (!this.petInfo) {
      this.addBreed();
    } else {
      this.petInfo?.breeds?.forEach((breed) => {
        this.breedFormArray.push(this.createBreed(breed.breed));
      });
      const petName = this.petInfo.nickName
        ? this.petInfo.nickName
        : this.petInfo.firstName + ' ' + this.petInfo.lastName;
      this.form.patchValue({
        name: [petName],
        firstName: this.petInfo.firstName,
        lastName: this.petInfo.lastName,
        weight: [
          this.petInfo.weight?.weight
            ? this.convertUnit.transform(this.petInfo.weight?.weight, 'g', 'lb')
            : null,
        ],
      });
      // TODO: Implement breed images
      /*
      this.breedImgList.forEach(b => {
        if (b.name === this.petInfo.breed[0]) {
          this.imgUrl = b.imgUrl;
        }
      })*/
    }
    this.form.valueChanges.subscribe((formValues) => {
      const tempExistBreedArr = formValues['breedArr'];
      let existBreedArr: string[] = [];
      for (let i = 0; i < tempExistBreedArr.length; i++) {
        if (tempExistBreedArr[i] && tempExistBreedArr[i].name && tempExistBreedArr[i].name.label) {
          existBreedArr.push(tempExistBreedArr[i].name.label.toLowerCase());
        }
      }
      this.breedList = this.wholeBreedList?.filter(
        (option) => !existBreedArr.includes(option?.label?.toLowerCase())
      );
    });
      
  }

  ngAfterViewChecked() {
    const isPetSelected = this.form.controls.isPetSelected.value;
    if (isPetSelected !== this.isPetSelected) {
      this.isPetSelected = isPetSelected;
      this.cdRef.detectChanges();
    }
  }

  // Public Methods
  // ------------------------------------------------------------------------------------

  get f() {
    return this.form.controls;
  }

  togglePetSelected() {
    this.form.patchValue({
      isPetSelected: !this.isPetSelected,
    });
  }

  // weight

  blurWeight() {
    const weightCtrl = this.form.get('weight');

    if (typeof weightCtrl.value !== 'number' || weightCtrl.value === 0) {
      weightCtrl.patchValue(null);
    } else if (weightCtrl.value < 0) {
      weightCtrl.patchValue(weightCtrl.value * -1);
    } else {
      weightCtrl.patchValue(parseFloat('' + weightCtrl.value));
    }
  }

  // agreement

  get agreementFormArray(): FormArray {
    return this.form.get('agreementArr') as FormArray;
  }

  createAgreement() {
    return this.fb.group({
      isSelected: [false],
    });
  }

  addAgreement() {
    this.agreementFormArray.push(this.createAgreement());
  }

  toggleAgreementItem(index: number) {
    this.agreementFormArray.at(index).setValue({
      isSelected: !this.isAgreementSelected(index),
    });
  }

  isAgreementSelected(index: number) {
    return this.agreementFormArray.at(index).get('isSelected').value;
  }

  // breed

  get breedFormArray(): FormArray {
    return this.form.get('breedArr') as FormArray;
  }

  createBreed(name?) {
    return this.fb.group({
      name: [name ? name : '', [Validators.required]],
    });
  }

  addBreed() {
    this._analytics.logLinkTap('interviewRequest_addBreed');
    this.breedFormArray.push(this.createBreed());
    this._manageFilteredBreedsByFormArrayIndex(this.breedFormArray.length - 1);
  }

  removeBreed(index) {
    this._analytics.logButtonTap('interviewRequest_removeBreed');
    this.breedFormArray.removeAt(index);
    this.filteredBreeds.splice(index, 1);
    this.filterBreedsOption();
  }

  getBreedLabel(breed: Breed): string {
    if (breed) {
      return breed.label;
    }
  }

  blurBreed(index: number) {
    const control = this.breedFormArray.at(index);
    if (typeof control?.value?.name !== 'object') {
      this.breedFormArray.at(index).patchValue({name:""});
    }
  }

  get breedNameList(): string {
    const breedStrings = this.petInfo.breeds
      ? this.petInfo.breeds.map((breed) => breed.breed.label)
      : [];
    if (breedStrings.length > 0) {
      return breedStrings.join(', ');
    } else {
      return 'unknown';
    }
  }

  // Private Methods
  // ------------------------------------------------------------------------------------

  // breeds

  private _filterBreeds(value: string): Breed[] {
    if (typeof value === 'string') {
      const filterValue = value?.toLowerCase();
      /*return this.breedList.label.filter(option => option.toLowerCase().includes(filterValue));*/
      return this.breedList.filter((breed) => {
        return breed.label.toLowerCase().includes(filterValue);
      });
    }
  }

  private _manageFilteredBreedsByFormArrayIndex(index: number): void {
    this.filteredBreeds[index] = this.breedFormArray
      .at(index)
      .get('name')
      .valueChanges.pipe(
        startWith(''),
        map((value) => this._filterBreeds(value))
      );
  }

  filterBreedsOption() {
    for (let i = 0; i < this.filteredBreeds.length; i++) {
      this._manageFilteredBreedsByFormArrayIndex(i);
    }
  }
}
